import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Receipt, ReceiptText, ArrowRight } from "lucide-react";

/**
 * Modal that asks "Spesa ordinaria vs Spesa ricorrente" before navigating to
 * the relevant creation form. Used by Cruscotto and Scadenze "+ Nuova scadenza"
 * entry points so users explicitly pick a workflow.
 */
export function NuovaScadenzaChoiceDialog({ open, onOpenChange }) {
  const navigate = useNavigate();

  const pick = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="nuova-scadenza-choice-dialog"
        className="max-w-xl"
      >
        <DialogHeader>
          <DialogTitle>Che tipo di scadenza vuoi creare?</DialogTitle>
          <DialogDescription>
            Le spese ordinarie e ricorrenti sono ora due flussi separati.
            Scegli quello adatto al caso operativo.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            data-testid="choice-ordinary"
            onClick={() => pick("/ordinary/new")}
            className="group flex flex-col items-start gap-2 rounded-xl border border-stone-200 bg-white p-4 text-left transition-shadow hover:border-stone-400 hover:shadow-[0_2px_8px_rgba(15,17,21,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-700/40"
          >
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-stone-700 text-white shadow-sm">
                <ReceiptText size={16} strokeWidth={1.8} />
              </div>
              <span className="text-sm font-semibold text-stone-900">
                Spesa ordinaria
              </span>
            </div>
            <p className="text-[12px] leading-relaxed text-stone-600">
              Fatture occasionali, bollette stimate, costi operativi variabili.
              Singola scadenza, niente serie.
            </p>
            <span className="mt-auto inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-stone-500 group-hover:text-stone-900">
              Crea spesa ordinaria <ArrowRight size={11} />
            </span>
          </button>

          <button
            type="button"
            data-testid="choice-recurring"
            onClick={() => pick("/expenses/new")}
            className="group flex flex-col items-start gap-2 rounded-xl border border-stone-200 bg-white p-4 text-left transition-shadow hover:border-stone-400 hover:shadow-[0_2px_8px_rgba(15,17,21,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-700/40"
          >
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
                <Receipt size={16} strokeWidth={1.8} />
              </div>
              <span className="text-sm font-semibold text-stone-900">
                Spesa ricorrente
              </span>
            </div>
            <p className="text-[12px] leading-relaxed text-stone-600">
              Affitti, abbonamenti, rate, piani fissi. Genera automaticamente le
              scadenze pianificate.
            </p>
            <span className="mt-auto inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-stone-500 group-hover:text-stone-900">
              Crea serie ricorrente <ArrowRight size={11} />
            </span>
          </button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
